import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { DriverForm } from '@src/driver/DriverForm';
import { useCreateDriver } from '@src/driver/hooks/useCreateDriver';
import { ConditionalFieldsContext } from '@src/orders/PermitFormSections/ConditionalFieldsContext';
import { DriverInformationInputsSchema } from '@src/orders/schemas/DriverInformationInputsSchema';
import {
  DriverEmailField,
  DriverIdField,
  DriverLicenseNumberField,
  DriverNameField,
  DriverPhoneField,
} from '@src/permitField/DriverInformationFields';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  onCreate: (newDriver: DriverListing) => void;
}

export const AddDriverForm = ({ clientId, onCreate }: Props) => {
  const { t } = useTranslation();
  const addForm = DriverInformationInputsSchema.omit([DriverIdField.fieldName]);
  const { control, handleSubmit } = useForm<yup.InferType<typeof addForm>>({
    resolver: yupResolver(addForm),
    defaultValues: {
      [DriverNameField.fieldName]: '',
      [DriverEmailField.fieldName]: '',
      [DriverPhoneField.fieldName]: '',
      [DriverLicenseNumberField.fieldName]: '',
    },
  });

  const { createDriver, isLoading } = useCreateDriver({
    clientId,
    onSuccess: (newDriver) => {
      onCreate(newDriver);
    },
  });

  const submitForm: SubmitHandler<
    yup.InferType<typeof DriverInformationInputsSchema>
  > = (data) => {
    createDriver(data);
  };

  return (
    <ConditionalFieldsContext.Provider
      value={{
        disabledFields: [],
      }}
    >
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(submitForm)(e);
        }}
      >
        <Stack gap={theme.spacing['3']}>
          <DriverForm control={control} />
          <Divider
            sx={{
              marginTop: theme.spacing['6'],
              marginBottom: theme.spacing['6'],
            }}
          />
          <Stack alignItems="flex-end">
            <Button disabled={isLoading} type="submit" variant="contained">
              {t('create')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </ConditionalFieldsContext.Provider>
  );
};
